import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import type { ProductionRun } from "./models";
import { productionGateway } from "./production-gateway";
import type { QuantityWriteDraft } from "./quantity-write-queue";
import type { LocalDraftRef } from "./quantity-outbox-store";
import { ApiError } from "../../shared/api/client";
import { criticalWriteFailureAction, type CriticalWriteDeliveryState } from "./critical-write-failure";
import {
  loadCriticalWrite,
  removeCriticalWrite,
  saveCriticalWrite,
  type CriticalWrite,
} from "./critical-write-store";

type HistoricalWrite = Extract<CriticalWrite, { kind: "HISTORICAL_PRODUCT_SCRAP" }>["payload"];
type PendingHistoricalWrite = { write: HistoricalWrite; deliveryState: CriticalWriteDeliveryState; createdAt: string };
type SupervisorApproval = { supervisorId: string; pin: string; occurredAt: string };
const stalePayloadCodes = new Set(["PRODUCTION_RUN_VERSION_CHANGED", "PRODUCTION_UNIT_CONTRACT_CHANGED", "PRODUCTION_UNIT_INVALID"]);
const historicalEntrySchema = z.object({
  id: z.string().min(1),
  type: z.enum(["WASTE", "SCRAP"]),
  quantity: z.number().positive(),
  unit: z.string().min(1),
  enteredBy: z.string().min(1),
  operatorSessionId: z.string().min(1),
  terminalId: z.string().min(1),
  occurredAt: z.string().datetime({ offset: true }),
  enteredAt: z.string().datetime({ offset: true }),
}).passthrough();
const receiptSchema = z.object({
  status: z.enum(["CONFIRMED", "NOT_FOUND"]),
  entry: historicalEntrySchema.nullable(),
}).strict();

function actualOccurrence(value: string): string {
  if (!value) throw new Error("Iskartanın gerçek oluşma zamanını girin.");
  const local = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/u.test(value);
  const parsed = new Date(local ? `${value}${value.length === 16 ? ":00" : ""}+03:00` : value);
  if (!Number.isFinite(parsed.getTime())) throw new Error("Gerçek oluşma zamanı geçerli değil.");
  return parsed.toISOString();
}

/** Post-completion writes are persisted separately and are never put into the automatic quantity outbox. */
export function useHistoricalScrapWrite({ run, terminalCode, sessionId, operatorId, preview, onSaved }: {
  run: ProductionRun;
  terminalCode: string;
  sessionId: string;
  operatorId: string;
  preview: boolean;
  onSaved: () => void;
}) {
  const [pending, setPending] = useState<PendingHistoricalWrite | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadCriticalWrite(preview, { terminalCode, operatorId, sessionId, runId: run.id }, "HISTORICAL_PRODUCT_SCRAP")
      .then((stored) => {
        if (active && stored?.kind === "HISTORICAL_PRODUCT_SCRAP") {
          const deliveryState = stored.deliveryState === "SENDING" ? "OUTCOME_UNKNOWN" : stored.deliveryState;
          setPending({ write: stored.payload, deliveryState, createdAt: stored.createdAt });
          if (deliveryState !== stored.deliveryState) {
            void saveCriticalWrite(preview, { ...stored, deliveryState }).catch((caught: unknown) => {
              if (active) setError(caught instanceof Error ? caught.message : "Bekleyen kayıt durumu cihazda güncellenemedi.");
            });
          }
        }
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "Bekleyen kritik kayıt okunamadı.");
      });
    return () => { active = false; };
  }, [operatorId, preview, run.id, sessionId, terminalCode]);

  const persistIntent = useCallback((intent: PendingHistoricalWrite, deliveryState: CriticalWriteDeliveryState) =>
    saveCriticalWrite(preview, { kind: "HISTORICAL_PRODUCT_SCRAP", idempotencyKey: intent.write.idempotencyKey,
      terminalCode, operatorId, sessionId, runId: run.id, createdAt: intent.createdAt, deliveryState, payload: intent.write }),
  [operatorId, preview, run.id, sessionId, terminalCode]);

  const send = useCallback(async (intent: PendingHistoricalWrite, approvalToken: string) => {
    setSaving(true);
    setError(null);
    const stateBeforeAttempt = intent.deliveryState;
    const sending = { ...intent, deliveryState: "SENDING" as const };
    try {
      await persistIntent(sending, "SENDING");
      setPending(sending);
    } catch (caught) {
      setSaving(false);
      setError(caught instanceof Error ? caught.message : "Kritik kayıt cihazda gönderime hazırlanamadı.");
      throw caught;
    }
    let entry: z.infer<typeof historicalEntrySchema>;
    try {
      const response: unknown = await productionGateway.addQuantity(run.id, { ...intent.write, approvalToken }, terminalCode);
      const parsed = historicalEntrySchema.safeParse(response);
      const matches = parsed.success && parsed.data.type === intent.write.type && parsed.data.quantity === intent.write.quantity
        && parsed.data.unit === intent.write.unit && parsed.data.enteredBy === operatorId
        && parsed.data.operatorSessionId === intent.write.expectedSessionId
        && Date.parse(parsed.data.occurredAt) === Date.parse(intent.write.occurredAt);
      if (!parsed.success || !matches) {
        throw new ApiError("Geçmiş ıskarta yanıtı doğrulanamadı. İşlem sonucu sunucudan kontrol edilmelidir.", "OPERATION_OUTCOME_UNKNOWN");
      }
      entry = parsed.data;
    } catch (caught) {
      try {
        if (criticalWriteFailureAction(stateBeforeAttempt, caught) === "REMOVE") {
          await removeCriticalWrite(preview, intent.write.idempotencyKey);
          setPending(null);
        } else {
          const uncertain = { ...intent, deliveryState: "OUTCOME_UNKNOWN" as const };
          await persistIntent(uncertain, "OUTCOME_UNKNOWN");
          setPending(uncertain);
        }
      } finally {
        setSaving(false);
      }
      setError(caught instanceof Error ? caught.message : "Geçmiş ıskarta kaydının sonucu doğrulanamadı. Aynı işlem kimliği korunuyor.");
      throw caught;
    }
    const confirmed = { ...intent, deliveryState: "REMOTE_CONFIRMED" as const };
    try {
      await persistIntent(confirmed, "REMOTE_CONFIRMED");
      setPending(confirmed);
      await removeCriticalWrite(preview, intent.write.idempotencyKey);
      setPending(null);
      onSaved();
      return entry;
    } catch (caught) {
      setPending(confirmed);
      setError("Kayıt sunucuda doğrulandı; cihazdaki bekleyen işaret temizlenemedi. Sonucu tekrar kontrol edin.");
      throw caught;
    } finally {
      setSaving(false);
    }
  }, [onSaved, persistIntent, preview, run.id, terminalCode]);

  const releasePreparedStaleApproval = useCallback(async (intent: PendingHistoricalWrite, caught: unknown) => {
    if (intent.deliveryState === "PREPARED" && caught instanceof ApiError && stalePayloadCodes.has(caught.code)) {
      await removeCriticalWrite(preview, intent.write.idempotencyKey);
      setPending(null);
    }
  }, [preview]);

  const authorizeAndSend = useCallback(async (intent: PendingHistoricalWrite, supervisor: Pick<SupervisorApproval, "supervisorId" | "pin">) => {
    if (!supervisor.supervisorId || !/^\d{4}$/u.test(supervisor.pin)) {
      throw new Error("Vardiya amirini seçin ve dört haneli PIN’ini girin.");
    }
    try {
      const approval = await productionGateway.approveTerminalAction(terminalCode, {
        ...intent.write,
        supervisorId: supervisor.supervisorId,
        pin: supervisor.pin,
        actionScope: "POST_COMPLETION_QUANTITY_SCRAP",
        targetRef: run.id,
      });
      return send(intent, approval.approvalToken);
    } catch (caught) {
      await releasePreparedStaleApproval(intent, caught);
      throw caught;
    }
  }, [releasePreparedStaleApproval, run.id, send, terminalCode]);

  const submit = useCallback(async (draft: QuantityWriteDraft, supervisor: SupervisorApproval, localDraft?: LocalDraftRef) => {
    if (pending) throw new Error("Önce bekleyen geçmiş ıskarta kaydının sonucunu doğrulayın.");
    if (run.status !== "COMPLETED" || (draft.type !== "WASTE" && draft.type !== "SCRAP")) {
      throw new Error("Bu akış yalnız tamamlanmış işin ıskarta kaydı içindir.");
    }
    const write: HistoricalWrite = {
      ...draft,
      quantity: Number(draft.quantity),
      occurredAt: actualOccurrence(supervisor.occurredAt),
      idempotencyKey: crypto.randomUUID(),
      expectedRunVersion: run.version,
      expectedSessionId: sessionId,
    };
    const createdAt = new Date().toISOString();
    await saveCriticalWrite(preview, {
      kind: "HISTORICAL_PRODUCT_SCRAP",
      idempotencyKey: write.idempotencyKey,
      terminalCode,
      operatorId,
      sessionId,
      runId: run.id,
      createdAt,
      deliveryState: "PREPARED",
      payload: write,
    }, localDraft);
    const intent: PendingHistoricalWrite = { write, deliveryState: "PREPARED", createdAt };
    setPending(intent);
    return authorizeAndSend(intent, supervisor);
  }, [authorizeAndSend, operatorId, pending, preview, run.id, run.status, run.version, sessionId, terminalCode]);

  const verifyPending = useCallback(async () => {
    if (!pending) return null;
    setSaving(true);
    setError(null);
    try {
      const receipt = receiptSchema.parse(await productionGateway.quantityReceipt(
        run.id,
        pending.write.idempotencyKey,
        pending.write.expectedSessionId,
        terminalCode,
      ));
      if (receipt.status === "CONFIRMED" && receipt.entry && receipt.entry.enteredBy === operatorId
        && receipt.entry.operatorSessionId === pending.write.expectedSessionId) {
        await removeCriticalWrite(preview, pending.write.idempotencyKey);
        setPending(null);
        onSaved();
        return receipt.entry;
      }
      setError("Sunucuda henüz kayıt bulunamadı. Aynı işlem kimliği korunuyor; yeniden amir onayı alınabilir.");
      return null;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Kayıt sonucu doğrulanamadı.");
      return null;
    } finally {
      setSaving(false);
    }
  }, [onSaved, operatorId, pending, preview, run.id, terminalCode]);

  const reauthorizePending = useCallback(async (supervisor: Pick<SupervisorApproval, "supervisorId" | "pin">) => {
    if (!pending) return null;
    return authorizeAndSend(pending, supervisor);
  }, [authorizeAndSend, pending]);

  return { submit, verifyPending, reauthorizePending, pending, saving, error };
}
